import { Link } from "react-router";
import { Shield, FileCheck, ArrowRight, CheckCircle, Search, Volume2 } from "lucide-react";
import { industryTopics } from "../data/industryTopics";
import { MarketingBottomCta } from "../components/MarketingBottomCta";
import { HubTopicCardGrid } from "../components/HubTopicCardGrid";
import { Seo } from "../components/Seo";
import { buildIndustryHubCards } from "../data/navMenus";

const FEATURED_SLUGS = ["banking", "pharma", "legal"] as const;

const featuredIndustries = FEATURED_SLUGS.map((slug) => industryTopics.find((t) => t.slug === slug)).filter(
  (t): t is NonNullable<typeof t> => Boolean(t),
);

const industryHubGroups = [{ heading: "All covered verticals", cards: buildIndustryHubCards() }];

export function Industries() {
  return (
    <>
      <Seo
        title="Industries"
        description="Industry coverage for regulated desktop session recording: banking, pharma, insurance, logistics, retail, legal, telecom, government, SaaS, and BPO. Optional CCTV+ camera add-on documented separately where relevant."
        canonicalPath="/industries"
      />
      <div className="bg-slate-950 min-h-screen">
        <section className="relative pt-36 pb-16 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 70% 50% at 50% 0%, rgba(13, 148, 136, 0.13) 0%, transparent 60%)",
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(148, 163, 184, 1) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 1) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-teal-500/25 bg-teal-500/5 text-teal-400 text-xs uppercase tracking-widest mb-8"
              style={{ fontWeight: 600 }}
            >
              <FileCheck size={12} />
              Industries & Verticals
            </span>
            <h1
              className="text-slate-100 mb-6"
              style={{
                fontSize: "clamp(2rem, 4.5vw, 3.5rem)",
                fontWeight: 800,
                letterSpacing: "-0.03em",
                lineHeight: "1.1",
              }}
            >
              Built for the world's{" "}
              <span
                style={{
                  background: "linear-gradient(135deg, var(--color-teal-500, #14b8a6) 0%, var(--color-emerald-500, #10b981) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                highest-risk environments.
              </span>
            </h1>
            <p className="text-on-dark-muted text-lg max-w-2xl mx-auto leading-relaxed">
              Pick your sector below. Each industry opens on its own page with risks, workflows, and optional CCTV+
              angles.
            </p>
          </div>
        </section>

        {/* Compact capability strip */}
        <section className="pb-8 border-b border-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-teal-500/20 bg-teal-500/5">
                <Search size={13} className="text-teal-400 shrink-0" />
                <p className="text-on-dark-muted text-xs leading-none">
                  <span className="text-teal-300 font-semibold">Pro and Enterprise:</span>{" "}
                  Basic OCR, Advanced OCR, and Chat with Your Recordings across every vertical.{" "}
                  <Link to="/features#ai-intelligence" className="text-teal-400 hover:text-teal-300 font-medium underline-offset-2 hover:underline">
                    AI features
                  </Link>
                </p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-emerald-500/20 bg-emerald-500/5">
                <Volume2 size={13} className="text-emerald-400 shrink-0" />
                <p className="text-on-dark-muted text-xs leading-none">
                  <span className="text-emerald-300 font-semibold">Session Audio</span>{" "}
                  and{" "}
                  <span className="text-violet-300 font-semibold">Webcam Capture</span>{" "}
                  optional add-ons.{" "}
                  <Link to="/pricing#webcam-capture-addon" className="text-teal-400 hover:text-teal-300 font-medium underline-offset-2 hover:underline">
                    Module pricing
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </section>

        <HubTopicCardGrid
          groups={industryHubGroups}
          sectionTitle="Choose your industry"
          sectionSubtitle="Any sector with compliance obligations, human operators, and costly error scenarios."
        />

        <section className="py-20 border-b border-slate-800/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2
                className="text-slate-100"
                style={{ fontSize: "clamp(1.5rem, 3vw, 2rem)", fontWeight: 800, letterSpacing: "-0.02em" }}
              >
                Featured verticals
              </h2>
              <p className="text-on-dark-muted mt-3 text-base max-w-xl mx-auto">
                Deeper snapshots for banking, life sciences, and legal. Open the full industry page for the complete
                brief.
              </p>
            </div>
            <div className="flex flex-col gap-8">
              {featuredIndustries.map((ind, i) => (
                <div
                  key={ind.slug}
                  id={ind.slug}
                  className={`rounded-2xl border overflow-hidden ${
                    ind.accentColor === "teal" ? "border-teal-500/15" : "border-emerald-500/15"
                  }`}
                  style={{ background: "linear-gradient(135deg, var(--color-slate-900) 0%, var(--color-slate-950) 100%)" }}
                >
                  <div className="grid lg:grid-cols-2">
                    <div className={`relative h-64 lg:h-auto overflow-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                      {ind.image && (
                        <img src={ind.image} alt={ind.label} className="w-full h-full object-cover opacity-40" />
                      )}
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            i % 2 === 1
                              ? "linear-gradient(to right, transparent, var(--color-slate-900) 90%)"
                              : "linear-gradient(to left, transparent, var(--color-slate-900) 90%)",
                        }}
                      />
                      <div className="absolute top-6 left-6">
                        <div
                          className={`w-12 h-12 rounded-xl ${ind.iconBg} flex items-center justify-center shadow-lg`}
                        >
                          <ind.icon size={22} className="text-white" />
                        </div>
                      </div>
                    </div>

                    <div className={`p-10 lg:p-14 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                      <span
                        className={`text-xs uppercase tracking-widest mb-4 block ${
                          ind.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                        }`}
                        style={{ fontWeight: 600 }}
                      >
                        {ind.label}
                      </span>
                      <h2
                        className="text-slate-100 mb-4"
                        style={{ fontSize: "1.7rem", fontWeight: 800, letterSpacing: "-0.02em", lineHeight: "1.2" }}
                      >
                        {ind.headline}
                      </h2>
                      <p className="text-on-dark-muted text-sm leading-relaxed mb-8">{ind.description}</p>

                      <div>
                        <p
                          className="text-on-dark-muted text-xs uppercase tracking-widest mb-4"
                          style={{ fontWeight: 600 }}
                        >
                          Key Risk Scenarios
                        </p>
                        <div className="grid sm:grid-cols-2 gap-3 mb-8">
                          {ind.keyRisks.map((risk) => (
                            <div key={risk} className="flex items-start gap-2">
                              <CheckCircle
                                size={14}
                                className={`mt-0.5 shrink-0 ${
                                  ind.accentColor === "teal" ? "text-teal-400" : "text-emerald-400"
                                }`}
                              />
                              <span className="text-on-dark-muted text-xs">{risk}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Link
                        to={`/industries/${ind.slug}`}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white text-sm transition-all duration-200 hover:shadow-lg ${
                          ind.accentColor === "teal"
                            ? "bg-teal-600 hover:bg-teal-500 hover:shadow-teal-500/25"
                            : "bg-emerald-600 hover:bg-emerald-500 hover:shadow-emerald-500/25"
                        }`}
                        style={{ fontWeight: 600 }}
                      >
                        <Shield size={15} />
                        View {ind.label} industry page
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MarketingBottomCta
          titleBefore="Serving finance, life sciences, operations, and more. "
          titleAccent="Where seeing what happened on screen is just the beginning."
          description="Tell us about your workflows and regulatory overlays. We'll map DeskReplay capture policies to the consoles where monitoring, security, and coaching all matter."
        />
      </div>
    </>
  );
}
